import React, { useState } from 'react';
import { useReactFlow, useNodeId } from '@xyflow/react';
import { Upload, AlertCircle, Loader2 } from 'lucide-react';
import { useTranslation } from '../../../../i18n';
import { uploadFile } from '../../../../utils/api';

const SUPPORTED_FORMATS = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

const UploadImageContent = ({ data }: { data: any }) => {
  const { updateNodeData } = useReactFlow();
  const nodeId = useNodeId();
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && nodeId) {
      setError(null);
      setUploading(true);
      setUploadProgress(0);

      if (!SUPPORTED_FORMATS.includes(file.type)) {
        setError(t('canvas.uploadImage.unsupportedFormat'));
        setUploading(false);
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        setError(t('canvas.uploadImage.fileTooLarge'));
        setUploading(false);
        return;
      }

      try {
        // Create a progress interval to smooth progress updates
        const progressInterval = setInterval(() => {
          setUploadProgress(prev => {
            // Increment by small amounts if progress is stuck
            if (prev < 90) {
              return prev + Math.min(5, 90 - prev);
            }
            return prev;
          });
        }, 200);

        const result = await uploadFile(file, (progress) => {
          clearInterval(progressInterval);
          setUploadProgress(progress);
        });

        if (result.code === 201) {
          updateNodeData(nodeId, {
            filename: result.data.name,
            url: result.data.url
          });
        } else {
          setError(result.message || '上传失败');
        }
      } catch (err: any) {
        setError(err.message || '上传失败，请重试');
      } finally {
        // Set to 100% when upload completes
        setUploadProgress(100);
        // Delay hiding the progress to show completion
        setTimeout(() => {
          setUploading(false);
          setUploadProgress(0);
        }, 500);
      }
    }
  };

  const triggerFileInput = () => {
    document.getElementById(`file-input-${nodeId}`)?.click();
  };

  return (
    <div className="flex flex-col gap-3 p-1">
      <div
        className="w-full aspect-square bg-slate-900 border border-slate-700 rounded flex items-center justify-center overflow-hidden relative group"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {data.url ? (
          <>
            <img src={data.url} alt="Uploaded" className="w-full h-full object-contain" />
            {isHovered && !uploading && (
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center transition-opacity duration-200">
                <button
                  onClick={triggerFileInput}
                  className="w-16 h-16 rounded-full bg-slate-700/60 hover:bg-slate-600/80 text-white/80 hover:text-white flex items-center justify-center transition-all duration-150 transform hover:scale-105"
                  title="Upload new image"
                >
                  <Upload size={24} className="text-white/80 hover:text-white" />
                </button>
              </div>
            )}
          </>
        ) : (
          <button
            onClick={triggerFileInput}
            className="w-16 h-16 rounded-full bg-slate-700/60 hover:bg-slate-600/80 text-white/80 hover:text-white flex items-center justify-center transition-all duration-150 transform hover:scale-105"
            title="Upload image"
          >
            <Upload size={24} className="text-white/80 hover:text-white" />
          </button>
        )}

        {/* Upload Progress Overlay */}
        {uploading && (
          <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center transition-opacity duration-200">
            <div className="relative mb-2">
              {/* Simplified Circular Progress */}
              <svg className="w-16 h-16 transform -rotate-90">
                <circle
                  cx="32"
                  cy="32"
                  r="28"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                  className="text-slate-700"
                />
                <circle
                  cx="32"
                  cy="32"
                  r="28"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                  strokeDasharray={`${uploadProgress * 1.76} 176`}
                  className="text-blue-500 transition-all duration-300"
                  strokeLinecap="round"
                />
              </svg>

              {/* Only show progress percentage, no duplicate loader */}
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-white text-sm font-medium">
                  {uploadProgress}%
                </span>
              </div>
            </div>

            {/* Status Text */}
            <div className="text-white text-xs mt-2">
              {uploadProgress < 100 ? 'Uploading...' : 'Complete!'}
            </div>
          </div>
        )}

        <input
          id={`file-input-${nodeId}`}
          type="file"
          accept=".jpg,.jpeg,.png,.gif,.webp"
          className="hidden"
          onChange={handleFileChange}
          disabled={uploading}
        />
      </div>

      {data.filename && <div className="text-[10px] text-slate-500 truncate text-center">{data.filename}</div>}
      {error && (
        <div className="flex items-start gap-1.5 text-[10px] text-red-400 bg-red-950/30 border border-red-900/50 rounded px-2 py-1.5 transition-all duration-200">
          <AlertCircle size={10} className="mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default UploadImageContent;
